<template>
  <div>
    <naj-box
      v-if="!selected"
      :title="local.bankruptcy.subRequests.subRequestObjections.objectionReplies"
      :disable-collapsing="true"
      :header-icon="null"
    >
      <v-container
        fluid
        class="pa-0"
        dir="rtl"
      >
        <template v-if="memos.length">
          <v-card
            v-for="(memo, i) in memos"
            :key="memo.objectKey"
            class="mb-4 pa-4 objection-row"
            outlined
          >
            <v-row
              no-gutters
              align-center
              justify="space-between"
              class="flex-row-reverse"
            >
              <v-col
                cols="12"
                md="2"
                class="text-right mb-2 mb-md-0"
              >
                <v-btn
                  outlined
                  x-large
                  block
                  class="reply-btn"
                  @click="open(memo)"
                >
                  {{ local.bankruptcy.subRequests.subRequestObjections.reply }}
                </v-btn>
              </v-col>

              <v-col
                cols="12"
                md="10"
              >
                <v-row
                  dense
                  class="text-right details-row"
                >
                  <v-col
                    cols="12"
                    md="2"
                  >
                    <div class="label">
                      {{ local.bankruptcy.subRequests.subRequestObjections.objectionNumber }}
                    </div>
                    <div class="value">
                      {{ memo && memo.id }}
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                  >
                    <div class="label">
                      {{ local.bankruptcy.subRequests.subRequestObjections.applicantName }}
                    </div>
                    <div class="value">
                      <template v-if="memo.isFirm">
                        {{ memo.partyDetails && memo.partyDetails.firmName }}
                      </template>
                      <template v-else>
                        {{
                          joinNames(
                            memo.partyDetails && memo.partyDetails.firstName,
                            memo.partyDetails && memo.partyDetails.fatherName,
                            memo.partyDetails && memo.partyDetails.familyName,
                          )
                        }}
                      </template>
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    md="2"
                  >
                    <div class="label">
                      {{ local.bankruptcy.subRequests.subRequestObjections.role }}
                    </div>
                    <div class="value">
                      {{ getLegalCapacityName(memo.partyDetails && memo.partyDetails.legalCapacityId, legalCapacities) }}
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    md="2"
                  >
                    <div class="label">
                      {{ local.bankruptcy.subRequests.subRequestObjections.idType }}
                    </div>
                    <div class="value">
                      <template v-if="memo.isFirm">
                        {{ firmIdTypeRaw(memo.partyDetails) }}
                      </template>
                      <template v-else>
                        {{ getIdTypeName(memo.partyDetails && memo.partyDetails.identityTypeId, identityTypes) }}
                      </template>
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                  >
                    <div class="label">
                      {{ local.bankruptcy.subRequests.subRequestObjections.idNumber }}
                    </div>
                    <div class="value">
                      <template v-if="memo.isFirm">
                        {{ firmIdNumber(memo.partyDetails) }}
                      </template>
                      <template v-else>
                        {{ (memo.partyDetails && memo.partyDetails.identityNumber) || '' }}
                      </template>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card>
        </template>

        <template v-else>
          <naj-no-data> </naj-no-data>
        </template>
      </v-container>
    </naj-box>

    <naj-box
      v-else
      :title="local.bankruptcy.subRequests.subRequestObjections.summaryOfObjectionResponsesWithApplicant"
      has-footer
    >
      <v-row
        v-for="m in [...selectedMemos]"
        :key="m.objectKey"
        class="mb-4"
      >

        <v-col v-if="!m.isReply" cols="6">
          <div class="objection-block objection row">
            <div class="col-6 judicial-department">
              <span>{{ getPartyName(m) }}</span>
              <span v-if="m.partyDetails" class="party-name">
                ({{ getLegalCapacityName(m.partyDetails.legalCapacityId, legalCapacities) }})
              </span>
            </div>
            <div class="col-6">
              <span class="float-left light-text">
                {{ toHijriDate(m.createDate) + " " + getTimePartArabic(m.createDate) }}
              </span>
            </div>
            <div class="col-12 card-label">
              {{local.bankruptcy.subRequests.subRequestObjections.objection}}
            </div>
            <div class="col-12">
              <span class="wrap-text">{{ m.memoText || m.text || '' }}</span>
            </div>
            <div
              v-if="m.attachments && m.attachments.length > 0"
              class="col-12 attachment-border"
              >
              <div
              class="col-12 mt-2 attachment-section"
              >
              <div class="attachment-label">
                {{local.caseFile.tabHeaders.documents}}
              </div>
              <attachment-list class="attachment-list"
              :attachments="m.attachments"
              :disableDelete="true"
              ></attachment-list>
            </div>
          </div>
          </div>
        </v-col>

        <v-col v-else cols="6">
        </v-col>

        <v-col v-if="m.isReply" cols="6">
          <div class="objection-block reply row">
            <div class="col-6 judicial-department">
              <span>{{ getPartyName(m) }}</span>
              <span
                v-if="m.partyDetails"
                class="party-name"
              >
                ({{ getLegalCapacityName(m.partyDetails.legalCapacityId, legalCapacities) }})
              </span>
            </div>
            <div class="col-6">
              <span class="float-left light-text">
                {{ toHijriDate(m.createDate) + " " + getTimePartArabic(m.createDate) }}
              </span>
            </div>
            <div class="col-12 card-label">
              {{local.bankruptcy.subRequests.subRequestObjections.replyOnObjection}}
            </div>
            <div class="col-12">
              <span class="wrap-text">{{ m.memoText || m.text || '' }}</span>
            </div>
            <div
              v-if="m.attachments && m.attachments.length > 0"
              class="col-12 attachment-border"
            >
              <div
                class="col-12 mt-2 attachment-section"
              >
              <div class="attachment-label">
                {{local.caseFile.tabHeaders.documents}}
              </div>
              <attachment-list class="attachment-list"
                :attachments="m.attachments"
                :disableDelete="true"
              ></attachment-list>
            </div>
          </div>
          </div>
        </v-col>


      </v-row>

      <!-- Reply Input Section in Footer -->
      <template v-slot:footer>
        <v-row class="footer-wrapper">

          <div
              v-if="uploadAttachments && uploadAttachments.length > 0"
              class="attachment-section-wrapper"
            >
            <div class="attachment-label">
              {{local.caseFile.tabHeaders.documents}}
            </div>
              <attachment-list 
                :attachments="uploadAttachments"
                :disableDelete="false"
              ></attachment-list>
            </div>

          <div class="row col-12 wrapper">
            <naj-input
              :type-id="formFieldType.textarea"
              :value.sync="replyText"
              :disabled="!canReply || !checkedCanReply"
              :label="local.bankruptcy.subRequests.subRequestObjections.replyOnObjection"
              :placeholder="local.bankruptcy.subRequests.subRequestObjections.WriteObjectionText"
              rows="1"
              validates="max:2000"
              class="reply-input"
            ></naj-input>
            
            
          </div>
          <div class="col-12 buttons-wrapper">
            <naj-button
              outlined
              type="button"
              color="primary"
              :disabled="!canReply || !checkedCanReply"
              @click="showGeneralAttachmentModal"
              >
              {{ local.bankruptcy.subRequests.subRequestObjections.AddAttachments }}
            </naj-button>
          
            <naj-button
              type="button"
              color="primary"
              :disabled="!canReply || !checkedCanReply || !replyTextTrimmed"
              @click="submitReply"
              >
              {{ local.bankruptcy.subRequests.subRequestObjections.objectionReply }}
            </naj-button>
          </div>

        </v-row>
      </template>

      <naj-confirmation
        :show-modal="showApplyConfirm"
        :is-persistent="true"
        :title="''"
        :show-close-btn="false"
        @cancel="showApplyConfirm = false"
      >
        <template #message-content>
          <h3 class="success-wrap">
            <v-icon
              size="75"
              color="var(--app-primary-color)"
            >
              mdi-check-circle
            </v-icon>
            <div class="success-text">
              {{ local.bankruptcy.subRequests.subRequestObjections.objectionApplyConfirmMessage }}
            </div>
          </h3>
        </template>

        <template #actions>
          <naj-button
            class="full-width"
            @click="handleSuccessBack"
          >
            {{ local.bankruptcy.subRequests.subRequestObjections.back }}
          </naj-button>
        </template>
      </naj-confirmation>

      <request-reason-modal
        :modal-visibility="showAttachmentModal"
        @addRequestReasonAttachment="handleAttachmentSubmit"
        @onClose="hideAttachmentModal"
      >
      </request-reason-modal>
    </naj-box>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { getTimePartArabic, toHijriDate } from '@/helpers/utils';
import { getCompanyIdNumber, getIdTypeName, getLegalCapacityName, joinNames } from '../../../helpers/utils';
import NajConfirmation from '@/components/shared/naj-confirmation.vue';
import AttachmentList from './attachments-list.vue';
import RequestReasonModal from '@/components/shared/request-reason-modal/index';

const { mapState: mapLUTState, mapActions: mapBankruptcyActions } = createNamespacedHelpers('BankruptcyModule');

export default {
  name: 'ObjectionsTab',
  components: { 
    NajConfirmation,
    AttachmentList,
    RequestReasonModal
  },
  props: {
    subRequestObjectKey: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapLUTState(['identityTypes', 'legalCapacities']),
    replyTextTrimmed() {
      return (this.replyText || '').trim().length > 0;
    },
  },
  data() {
    return {
      selected: null,
      selectedMemos: [],
      canReply: false,
      showApplyConfirm: false,
      checkedCanReply: false,
      replyText: '',
      memos: [],
      uploadAttachments: [],
      maxFileSize: 5,
      loading: false,
      showAttachmentModal: false,
    };
  },
  created() {
    this.getIdentityTypes();
    this.getLegalCapacities();
    this.getMemos();
  },
  methods: {
    ...mapBankruptcyActions([
      'getSubRequestMemosReplies',
      'getIdentityTypes',
      'getLegalCapacities',
      'canReplyToSubRequestMemo',
      'submitSubRequestMemoReply',
      'getSubRequestMemos',
    ]),
    getMemos() {
      this.getSubRequestMemos(this.subRequestObjectKey).then((res) => {
        this.memos = res.data;
      });
    },
    async open(memo) {
      this.selected = memo;
      await this.loadThread();
      await this.checkCanReply();
      this.$emit('reply', memo);
    },
    async loadThread() {
      try {
        const res = await this.getSubRequestMemosReplies(this.selected.objectKey);
        const list = res.data;
        this.selectedMemos = Array.isArray(list) ? list : [];
      } catch (e) {
        this.selectedMemos = [];
      }
    },
    cancel() {
      this.selected = null;
      this.selectedMemos = [];
      this.replyText = '';
      this.canReply = false;
      this.checkedCanReply = false;
    },
    getPartyName(memo) {
      if (!memo || !memo.partyDetails) return '';
      if (memo.isFirm) {
        return memo.partyDetails.firmName || '';
      }
      return joinNames(
        memo.partyDetails.firstName,
        memo.partyDetails.fatherName,
        memo.partyDetails.familyName
      ) || '';
    },
    handleSuccessBack() {
      this.showApplyConfirm = false;
      this.getMemos();
      this.cancel();
    },
    async checkCanReply() {
      this.checkedCanReply = false;
      this.canReply = false;
      try {
        const res = await this.canReplyToSubRequestMemo(this.selected.objectKey);
        const v = res && typeof res.data !== 'undefined' ? res.data : res;
        this.canReply = !!v;
      } catch (e) {
        this.toaster?.error?.(this.local.common.generalError);
      } finally {
        this.checkedCanReply = true;
      }
    },
    async submitReply() {
      const text = (this.replyText || '').trim();
      if (!text || !this.selected) return;

      const memoObjectKey = this.selected.objectKey;
      const last = this.selectedMemos && this.selectedMemos.length ? this.selectedMemos[this.selectedMemos.length - 1] : null;

      const parentMemoObjectKey = last ? last.objectKey : this.selected.objectKey;
      try {
        await this.submitSubRequestMemoReply({
          memoObjectKey,
          text,
          parentMemoObjectKey,
          attachments: this.uploadAttachments,
        });
        this.replyText = '';
        this.uploadAttachments = [];
        await this.loadThread();
        await this.checkCanReply();
        this.showApplyConfirm = true;
      } catch (e) {
        this.toaster?.error?.(e?.message);
      }
    },
    hideAttachmentModal() {
      this.showAttachmentModal = false;
    },
    showGeneralAttachmentModal() {
      this.showAttachmentModal = true;
    },
    async handleAttachmentSubmit(attachment) {
      const displayedAttachment = {
        fileId: attachment.sharePointId || attachment.fileId,
        fileName: attachment.sharePointFileName || attachment.fileName || attachment.name,
        extension: attachment.extension,
        contentType: attachment.fileType,
        fileTypeId: attachment.fileTypeId,
      };
      if (!this.uploadAttachments) this.uploadAttachments = [];
      this.uploadAttachments.push(displayedAttachment);
      this.$set(this, 'uploadAttachments', this.uploadAttachments);
      this.hideAttachmentModal();
    },
    firmIdNumber(partyDetails) {
      if (!partyDetails) return '';
      return partyDetails.unifiedNumber || partyDetails.commercialRegistrationNumber || '';
    },
    firmIdTypeRaw(partyDetails) {
      if (!partyDetails) return '';
      return partyDetails.unifiedNumber
        ? this.local.bankruptcy.subRequests.subRequestObjections.unifiedNumber
        : partyDetails.commercialRegistrationNumber
          ? this.local.bankruptcy.subRequests.subRequestObjections.commercialRegistration
          : '';
    },

    getCompanyIdNumber,
    getIdTypeName,
    getLegalCapacityName,
    toHijriDate,
    getTimePartArabic,
    joinNames,
  },
};
</script>

<style lang="scss" scoped>
.objection-row {
  border: 1px solid #dbdbdb;
}

.card-label {
  font-size: 16px;
  border-top: 1px solid #dadada;
  margin-bottom: 4px;
}

.attachment-border{
  border-top: 1px solid #dadada;
  padding: 0px;
  padding-top: 16px;
}

.attachment-label {
  font-size: 16px;
  padding: 8px 0px 8px 0px;
  color: var(--app-primary-color);
  font-weight: 600;
}

.attachment-section {
  background: #fff;
  border-radius: 6px;
}

.enhanced-objection-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 6px 0 #ededed;
  border: 1px solid #dadada;
}

.attachment-list {
  gap: 10px;
  border-left: none !important;
}

.attachment-item {
  background: #f5f6fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.97em;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.attachment-item .file-name {
  color: #2b3748;
  margin-right: 2px;
}

.label {
  display: block;
  margin: 0 0 4px 0;
  color: var(--app-primary-color);
  font-weight: 700;
  text-align: right;
  direction: rtl;
}

.value {
  display: block;
  color: #14353c;
  font-weight: 600;
  text-align: right;
  direction: rtl;
}

.reply-btn {
  font-weight: 600;
  color: var(--app-primary-color);
}

.text-h6 {
  font-weight: 600;
  color: #14353c;
  margin-bottom: 8px;
}

.success-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.success-text {
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
}

.full-width {
  width: 100%;
}

.footer-wrapper {
  padding: 16px !important;
}

.objection-block {
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 14px;
  font-weight: bold;
  padding: 15px;
}

.objection-block.reply {
  background-color: #f8f8f9;
}

.objection-block.objection {
  background-color: #f8f8f9;
  margin-left: 10px;
}

.objection-block .judicial-department span {
  color: #009587;
}

.party-name {
  color: #96a4b1 !important;
  margin-right: 8px;
}

.light-text{
  color: #96a4b1 !important;
}

.wrapper {
  margin: 0;
  padding: 0;
}

.attachment-section-wrapper {
  width: 100%;
  margin-bottom: 20px;
}

::v-deep .input-container {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  width: auto;
}

::v-deep .naj-box-footer {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.reply-input {
  flex: 1 1 auto;
}

.buttons-wrapper {
  padding: 16px 0px 0px 0px;
  display: flex;
  justify-content: space-between;
}

.wrap-text {
  overflow-wrap: break-word;
}

@media (max-width: 960px) {
  .enhanced-objection-card {
    padding: 8px !important;
  }
  .reply-btn {
    width: 100%;
  }
}
</style>
